import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState, useEffect } from "react";

type DateFilterType = "today" | "yesterday" | "mtd" | "custom";

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangeFilterProps {
  onFilterChange: (filter: DateFilterType, dateRange?: DateRange) => void;
  onStatusChange?: (status: number) => void;
  onOrderTypeChange?: (orderType: string | null) => void;
}

export const DateRangeFilter = ({ onFilterChange, onStatusChange, onOrderTypeChange }: DateRangeFilterProps) => {
  const [selectedFilter, setSelectedFilter] = useState<DateFilterType>("today");
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  // Order Type: "all" = All (null), "Customer" = Customer, "SalesMan" = SalesMan
  const [orderType, setOrderType] = useState<string>("all");
  // 0 = ALL, 1 = Pending, 2 = Bill
  const [status, setStatus] = useState<"0" | "1" | "2">("1");
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  const handleFilterChange = (filter: DateFilterType) => {
    setSelectedFilter(filter);
    if (filter !== "custom") {
      setDateRange({ from: undefined, to: undefined });
      setFromDateInput("");
      setToDateInput("");
      onFilterChange(filter);
    } else {
      // Set default to today when custom is selected
      const today = new Date();
      const newRange = { from: today, to: today };
      setDateRange(newRange);
      setFromDateInput(format(today, "dd/MM/yyyy"));
      setToDateInput(format(today, "dd/MM/yyyy"));
      onFilterChange("custom", newRange);
    }
  };

  const parseDateInput = (input: string): Date | undefined => {
    if (!input.trim()) return undefined;
    // Try to parse dd/MM/yyyy format
    const parsed = parse(input, "dd/MM/yyyy", new Date());
    if (isValid(parsed)) {
      return parsed;
    }
    return undefined;
  };

  const handleFromDateInputChange = (value: string) => {
    setFromDateInput(value);
    const parsed = parseDateInput(value);
    if (parsed) {
      const newRange = { ...dateRange, from: parsed };
      setDateRange(newRange);
      if (newRange.from && newRange.to) {
        onFilterChange("custom", newRange);
      }
    } else if (value === "") {
      const newRange = { ...dateRange, from: undefined };
      setDateRange(newRange);
    }
  };

  const handleToDateInputChange = (value: string) => {
    setToDateInput(value);
    const parsed = parseDateInput(value);
    if (parsed) {
      const newRange = { ...dateRange, to: parsed };
      setDateRange(newRange);
      if (newRange.from && newRange.to) {
        onFilterChange("custom", newRange);
      }
    } else if (value === "") {
      const newRange = { ...dateRange, to: undefined };
      setDateRange(newRange);
    }
  };

  const handleFromDateSelect = (date: Date | undefined) => {
    const newRange = { ...dateRange, from: date };
    setDateRange(newRange);
    if (date) {
      setFromDateInput(format(date, "dd/MM/yyyy"));
    }
    setShowFromCalendar(false);
    if (newRange.from && newRange.to) {
      onFilterChange("custom", newRange);
    }
  };

  const handleToDateSelect = (date: Date | undefined) => {
    const newRange = { ...dateRange, to: date };
    setDateRange(newRange);
    if (date) {
      setToDateInput(format(date, "dd/MM/yyyy"));
    }
    setShowToCalendar(false);
    if (newRange.from && newRange.to) {
      onFilterChange("custom", newRange);
    }
  };

  // Notify parent of initial orderType value on mount (only if onOrderTypeChange is provided)
  useEffect(() => {
    if (onOrderTypeChange) {
      onOrderTypeChange(null); // "all" maps to null
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
      {/* Order Type dropdown: All / Customer / SalesMan - Only show if onOrderTypeChange is provided */}
      {onOrderTypeChange && (
        <div className="flex items-center gap-2">
          <Select
            value={orderType}
            onValueChange={(value) => {
              setOrderType(value);
              // Inform parent about orderType change ("all" = All/null, "Customer" = Customer, "SalesMan" = SalesMan)
              const orderTypeValue = value === "all" ? null : value;
              onOrderTypeChange?.(orderTypeValue);
            }}
          >
            <SelectTrigger className="w-[140px] h-10 shadow-sm border-border/50 hover:border-primary/50 transition-colors">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Customer">Customer</SelectItem>
              <SelectItem value="SalesMan">SalesMan</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Status dropdown: ALL / Pending / Bill */}
      <div className="flex items-center gap-2">
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value as "0" | "1" | "2");
            // Inform parent about status change (0 = ALL, 1 = Pending, 2 = Bill)
            const numericStatus = Number(value);
            if (!Number.isNaN(numericStatus)) {
              onStatusChange?.(numericStatus);
            }
          }}
        >
          <SelectTrigger className="w-[140px] h-10 shadow-sm border-border/50 hover:border-primary/50 transition-colors">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">All</SelectItem>
            <SelectItem value="1">Pending</SelectItem>
            <SelectItem value="2">Billing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Select value={selectedFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[180px] h-10 shadow-sm border-border/50 hover:border-primary/50 transition-colors">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="mtd">Month to Date</SelectItem>
            <SelectItem value="custom">Custom Date</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selectedFilter === "custom" && (
        <div className="flex items-center gap-3 bg-card/50 border border-border/50 rounded-lg px-4 py-2.5 shadow-sm backdrop-blur-sm">
          <div className="relative group">
            <label className="absolute -top-2 left-2 px-1.5 text-xs font-medium text-muted-foreground bg-background">
              From
            </label>
            <Input
              type="text"
              placeholder="dd/mm/yyyy"
              value={fromDateInput}
              onChange={(e) => handleFromDateInputChange(e.target.value)}
              className="w-[150px] h-10 pr-10 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
            />
            <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "absolute right-0 top-0 h-full px-3 hover:bg-primary/10 rounded-l-none",
                    "text-muted-foreground hover:text-primary transition-colors"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFromCalendar(!showFromCalendar);
                  }}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 shadow-lg border-border/50" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.from}
                  onSelect={handleFromDateSelect}
                  initialFocus
                  className="pointer-events-auto rounded-md"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted/50">
            <span className="text-xs font-medium text-muted-foreground">→</span>
          </div>

          <div className="relative group">
            <label className="absolute -top-2 left-2 px-1.5 text-xs font-medium text-muted-foreground bg-background">
              To
            </label>
            <Input
              type="text"
              placeholder="dd/mm/yyyy"
              value={toDateInput}
              onChange={(e) => handleToDateInputChange(e.target.value)}
              className="w-[150px] h-10 pr-10 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
            />
            <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "absolute right-0 top-0 h-full px-3 hover:bg-primary/10 rounded-l-none",
                    "text-muted-foreground hover:text-primary transition-colors"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowToCalendar(!showToCalendar);
                  }}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 shadow-lg border-border/50" align="start">
                <Calendar
                  mode="single"
                  selected={dateRange.to}
                  onSelect={handleToDateSelect}
                  initialFocus
                  disabled={(date) => dateRange.from ? date < dateRange.from : false}
                  className="pointer-events-auto rounded-md"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
};
